import React from 'react'
import { Grid, IconButton, Stack, Typography, FormHelperText, Button, useMediaQuery, useTheme } from '@mui/material'
import { J2Multiply } from '@e3dc-react/icons'
import { useFormContext, useFormState } from 'react-hook-form'
import get from 'lodash/get'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useApp } from 'src/Hooks'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import DaySelector from 'src/Libs/inputs/react-hook-form/DaySelector/DaySelector'
import { UnitTypes } from 'src/Libs/types'
import { DynamicTariffSettingsFormFields, ManualPowerPriceTranslationKey } from 'src/Libs/dynamic-tariff-settings/constants'
import DynamicTariffTimerPicker from '../TimeFrameList/DynamicTariffTimerPicker'

const CUSTOM_XS_BREAKPOINT = 380

interface Props {
  index: number
  onRemove: () => void
  hasWritingRights: boolean
  isRemoveDisabled: boolean
}

/**
 * A single manual power price period: weekday selection, a time range and a price.
 */
const ManualPowerPriceRow: React.FC<Props> = ({ index, onRemove, isRemoveDisabled, hasWritingRights }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { isMobile: isDownSm } = useApp()
  const isDownXs = useMediaQuery(theme.breakpoints.down(CUSTOM_XS_BREAKPOINT))
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'))
  const { control } = useFormContext()

  const rowName = `${DynamicTariffSettingsFormFields.PowerPrices}.${index}`
  const weekdaysName = `${rowName}.weekdays`

  /* the WeekDaySelector does not render its own validation error, so surface it here */
  const { errors } = useFormState({ control, name: weekdaysName })
  const weekdaysError = get(errors, weekdaysName) as { message?: string } | undefined

  const weekdaysField = (
    <Stack>
      <DaySelector name={weekdaysName} disabled={!hasWritingRights} disabledDays={[]} sx={{ flexWrap: 'wrap' }} />
      {weekdaysError?.message && <FormHelperText error>{weekdaysError.message}</FormHelperText>}
    </Stack>
  )
  const fromField = <DynamicTariffTimerPicker name={`${rowName}.from`} disabled={!hasWritingRights} fullWidth={isUpLg} />
  const toField = <DynamicTariffTimerPicker name={`${rowName}.to`} disabled={!hasWritingRights} fullWidth={isUpLg} allowEndOfDayMinute />
  const priceField = (
    <NumberField
      disabled={!hasWritingRights}
      name={`${rowName}.price`}
      suffix={UnitTypes.centsPerKiloWattHour}
      fullWidth={isDownXs || isUpLg}
      inputProps={{ style: { textAlign: 'right' } }}
    />
  )
  const removeButton = !isDownSm ? (
    <IconButton
      disabled={!hasWritingRights || isRemoveDisabled}
      sx={{ color: 'grey.grey00' }}
      onClick={onRemove}
      data-qa={`manual-power-price-remove-${index}`}
    >
      <J2Multiply />
    </IconButton>
  ) : (
    <Button color="error" disabled={!hasWritingRights || isRemoveDisabled} sx={{ width: isDownSm ? '100%' : 'fit-content' }} onClick={onRemove}>
      {t('common.remove')}
    </Button>
  )

  if (isDownSm) {
    return (
      <Grid container spacing={0} rowGap={2}>
        <Grid item xs={12}>
          <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Weekdays)}</Typography>
        </Grid>
        <Grid item xs={12}>
          {weekdaysField}
        </Grid>

        <Grid item xs={isDownXs ? 12 : 6} display="flex" alignItems="center">
          <Typography variant="body1">{t(ManualPowerPriceTranslationKey.From)}</Typography>
        </Grid>
        <Grid item xs={isDownXs ? 12 : 6}>
          {fromField}
        </Grid>

        <Grid item xs={isDownXs ? 12 : 6} display="flex" alignItems="center">
          <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Until)}</Typography>
        </Grid>
        <Grid item xs={isDownXs ? 12 : 6}>
          {toField}
        </Grid>

        <Grid item xs={isDownXs ? 12 : 6} display="flex" alignItems="center">
          <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Price)}</Typography>
        </Grid>
        <Grid item xs={isDownXs ? 12 : 6}>
          {priceField}
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="end">
          {removeButton}
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={5}>
        {weekdaysField}
      </Grid>
      <Grid item xs={2}>
        {fromField}
      </Grid>
      <Grid item xs={2}>
        {toField}
      </Grid>
      <Grid item xs={3}>
        <Stack direction="row" gap={{ sm: 2 }} alignItems="center">
          {priceField}
          {removeButton}
        </Stack>
      </Grid>
    </Grid>
  )
}

export default ManualPowerPriceRow
